import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const faqs = [
  {
    q: "How does Filter Hotels compare prices from multiple vendors?",
    a: "Filter Hotels integrates with multiple hotel booking vendors through secure APIs. When you search for a hotel, we query all connected vendors in real-time and display their pricing side by side for the same property. This allows you to instantly see which vendor offers the best rate without manually checking each platform.",
  },
  {
    q: "Why do prices differ for the same hotel across vendors?",
    a: "Different vendors negotiate their own contracts with hotels, have varying commission structures, and may offer different room types or cancellation policies. These factors result in price variations. Filter Hotels makes these differences transparent so you can choose the option that best fits your needs and budget.",
  },
  {
    q: "Is Filter Hotels only for businesses, or can individuals use it?",
    a: "Filter Hotels is designed specifically for B2B users — travel agents, corporate travel managers, and hospitality businesses. Our platform includes features like bulk booking management, agent dashboards, token-based payments, and support ticketing that cater to business needs. Individual travelers may find traditional consumer booking sites more suitable.",
  },
  {
    q: "How do I book a hotel through Filter Hotels?",
    a: "After registering and getting approved by our admin team, you can search for hotels, compare vendor prices, select your preferred option, and complete the booking directly through our platform. All bookings are processed securely, and you will receive instant confirmation along with booking vouchers.",
  },
  {
    q: "What payment methods does Filter Hotels support?",
    a: "We support multiple payment methods including online payments, manual bank transfers, and a token-based wallet system for agents. Tokens can be recharged by support or admin teams, and you can use them to pay for bookings instantly. This provides flexibility and faster transaction processing for high-volume users.",
  },
  {
    q: "What happens if I need to cancel a booking?",
    a: "Cancellation policies vary by vendor and hotel. When you view a hotel details page, you will see the specific cancellation policy for each vendor rate. If you need to cancel, you can do so through your dashboard. Refunds (if applicable) are processed according to the vendor policy and typically credited back to your account or wallet.",
  },
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (idx) => setOpenIndex(openIndex === idx ? null : idx);

  return (
    <section id="faq" className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block text-blue-600 text-sm font-semibold uppercase tracking-widest mb-3">
            FAQ
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600">
            Everything you need to know about Filter Hotels
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.08 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
            >
              <button
                onClick={() => toggle(idx)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
              >
                <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                <motion.div
                  animate={{ rotate: openIndex === idx ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </motion.div>
              </button>

              <AnimatePresence>
                {openIndex === idx && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 text-center"
        >
          <p className="text-gray-600 mb-4">Still have questions?</p>
          <a
            href="#contact"
            className="inline-block px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg"
          >
            Contact Us
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
